import api from "./api";
import { readProducts, readProduct, readEvents, readEvent } from "./db";

/**
 * Try the network first; if offline / fails, serve from IndexedDB.
 * These wrappers cover the read paths the app uses to operate offline.
 */

function isNetworkError(err) {
  if (!err) return false;
  if (err.message === "Network Error") return true;
  if (err.code === "ERR_NETWORK") return true;
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  return false;
}

function isUsableProduct(d) {
  return d && typeof d === "object" && typeof d.product_id === "string";
}
function isUsableProductList(d) {
  return Array.isArray(d) && (d.length === 0 || isUsableProduct(d[0]));
}
function isUsableEvent(d) {
  return d && typeof d === "object" && typeof d.event_id === "string";
}
function isUsableEventList(d) {
  return Array.isArray(d) && (d.length === 0 || isUsableEvent(d[0]));
}
function isUsableLookup(d) {
  return d && typeof d === "object" && d.product && d.variant;
}

export async function fetchProducts() {
  try {
    const { data } = await api.get("/products");
    if (!isUsableProductList(data)) throw new Error("Bad shape");
    return { data, source: "network" };
  } catch (e) {
    const cached = await readProducts();
    return { data: cached, source: "cache" };
  }
}

export async function fetchProduct(id) {
  try {
    const { data } = await api.get(`/products/${id}`);
    if (!isUsableProduct(data)) throw new Error("Bad shape");
    return { data, source: "network" };
  } catch (e) {
    const cached = await readProduct(id);
    if (cached) return { data: cached, source: "cache" };
    throw e;
  }
}

export async function fetchEvents() {
  try {
    const { data } = await api.get("/events");
    if (!isUsableEventList(data)) throw new Error("Bad shape");
    return { data, source: "network" };
  } catch (e) {
    const cached = await readEvents();
    return { data: cached, source: "cache" };
  }
}

export async function fetchEvent(id) {
  try {
    const { data } = await api.get(`/events/${id}`);
    if (!isUsableEvent(data)) throw new Error("Bad shape");
    return { data, source: "network" };
  } catch (e) {
    const cached = await readEvent(id);
    if (cached) return { data: cached, source: "cache" };
    throw e;
  }
}

export async function lookupCode(code) {
  try {
    const { data } = await api.get(`/products/lookup/barcode/${encodeURIComponent(code)}`);
    if (!isUsableLookup(data)) throw new Error("Bad shape");
    return { data, source: "network" };
  } catch (e) {
    const products = await readProducts();
    for (const p of products) {
      const v = (p.variants || []).find((x) => x.barcode === code || x.sku === code);
      if (v) return { data: { product: p, variant: v }, source: "cache" };
    }
    throw e;
  }
}

// keep export for any future callers
export { isNetworkError };
